import { Box, Drawer, Stack, useMediaQuery, useTheme } from "@mui/material";
import React from "react";
import { useLocation } from "react-router-dom";
import Logo from "src/assets/Logo";
import { useAuth } from "src/auth/AuthContext";
import Feature from "src/common_constants/Feature";
import Permissions from "src/common_constants/Permission";
import { useAppSelector } from "src/redux/hooks";
import { RootState } from "src/redux/store";
import IconsComponent from "src/shared_components/Icons/IconsComponent";
import PermissionRestrictedComponent from "src/shared_components/PermissionRestrictedComponent";
import { Scrollbar } from "src/shared_components/ScrollBar";
import "./sideNav.css";
import { SideNavItem } from "./SideNavItem";

interface SideNavProps {
  open: boolean;
  onClose: () => void;
  sideNavWidth: number;
  topNavHeight: number;
  toggleSideNav: () => void;
}

export const SideNav: React.FC<SideNavProps> = (props) => {
  const { open, onClose, sideNavWidth, topNavHeight, toggleSideNav } = props;
  const location = useLocation();
  const theme = useTheme();

  const lgUp: boolean = useMediaQuery(theme.breakpoints.up("lg"));
  const auth = useAuth();

  const items = [
    {
      title: "Dashboard",
      path: "/Dashboard",
      icon: <IconsComponent iconName="dashboard" fontSize="medium" />,
      requiredPermission: Permissions.DashboardView,
    },
    {
      title: "Customers",
      path: "/Customer",
      icon: <IconsComponent iconName="customer" fontSize="medium" />,
      requiredPermission: Permissions.CustomerView,
    },
    {
      title: "Vehicles",
      path: "/Asset",
      icon: <IconsComponent iconName="asset" fontSize="medium" />,
      requiredPermission: Permissions.CustomerView,
    },
    {
      title: "Reservations",
      path: "/Reservation",
      icon: <IconsComponent iconName="reservation" fontSize="medium" />,
      requiredPermission: Permissions.BookingView,
    },
    {
      title: "Contracts",
      path: "/Contract",
      icon: <IconsComponent iconName="contract" fontSize="medium" />,
      requiredPermission: Permissions.BookingView,
    },
    {
      title: "Invoices",
      path: "/Invoice",
      icon: <IconsComponent iconName="contract" fontSize="medium" />,
      requiredPermission: Permissions.InvoiceView,
    },
    {
      title: "Claims",
      path: "/Claim",
      icon: <IconsComponent iconName="contract" fontSize="medium" />,
      requiredPermission: Permissions.InsuranceClaimView,
      requiredFeature: Feature.CLAIM_MODULE,
    },
    {
      title: "Reports",
      path: "/Reports",
      icon: <IconsComponent iconName="report" fontSize="medium" />,
      requiredPermission: Permissions.CustomerView,
    },
    {
      title: "Settings",
      path: "/Settings",
      icon: <IconsComponent iconName="setting" fontSize="medium" />,
      requiredPermission: Permissions.CustomerView,
    },
    // {
    //   title: "Calendar",
    //   path: "/Calendar",
    //   icon: (
    //     <SvgIcon fontSize="medium">
    //       <CogIcon />
    //     </SvgIcon>
    //   ),
    // },
  ];

  const hideSideNavLables = sideNavWidth < 280 && lgUp;

  const features = useAppSelector(
    (state: RootState) => state.features.features,
  );

  const filteredItems = items.filter((item: any) => {
    if (item.requiredFeature !== undefined) {
      return features.includes(item.requiredFeature);
    }
    return true;
  });

  const content = (
    <Scrollbar
      sx={{
        height: "100%",
        "& .simplebar-content": {
          height: "100%",
        },
        "& .simplebar-scrollbar:before": {
          background: "neutral.400",
        },
        overflowX: "hidden",
        overflowY: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        <Box
          sx={{
            cursor: "pointer",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            boxShadow: "-11px 7px 40px 9px #0000000D",
          }}
          onClick={lgUp ? toggleSideNav : undefined}
          component={Box}
        >
          <Box
            sx={{
              display: "inline-flex",
              height: topNavHeight,
              p: 2,
            }}
          >
            {auth.logo?.url ? (
              <img
                src={auth?.logo?.url}
                alt={"Client Logo"}
                style={{
                  width: "100%",
                  height: "auto",
                  maxWidth: "100%",
                  objectFit: "contain",
                }}
              />
            ) : (
              <Logo />
            )}
          </Box>
        </Box>

        <Box
          component="nav"
          sx={{
            flexGrow: 1,
          }}
        >
          <Stack
            component="ul"
            spacing={0.5}
            sx={{
              listStyle: "none",
              p: 0,
              m: 0,
            }}
          >
            {filteredItems.map((item: any) => {
              const active = !!location.pathname
                .toLowerCase()
                .startsWith(item.path.toLowerCase());
              return (
                <PermissionRestrictedComponent
                  permission={item.requiredPermission}
                  key={item.title}
                >
                  <SideNavItem
                    active={active}
                    disabled={item.disabled}
                    external={item.external}
                    icon={item.icon}
                    key={item.title}
                    path={item.path}
                    title={item.title}
                    hideSideNavLables={hideSideNavLables}
                  />
                </PermissionRestrictedComponent>
              );
            })}
          </Stack>
        </Box>
      </Box>
    </Scrollbar>
  );

  // if (lgUp) {
  //   return (
  //     <Drawer
  //       anchor="left"
  //       open
  //       PaperProps={{
  //         sx: {
  //           backgroundColor: "#ffffff",
  //           color: "neutral.500",
  //           width: sideNavWidth,
  //           transition: "0.3s",
  //           transitionTimingFunction: "ease-in-out",
  //         },
  //       }}
  //       variant="permanent"
  //     >
  //       {content}
  //     </Drawer>
  //   );
  // }

  return (
    <Drawer
      anchor="left"
      onClose={onClose}
      open={open}
      PaperProps={{
        sx: {
          backgroundColor: "#08163B",
          color: "neutral.500",
          width: 280,
        },
      }}
      sx={{ zIndex: (theme) => theme.zIndex.appBar + 100 }}
      variant="temporary"
    >
      {content}
    </Drawer>
  );
};
